import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosRequestConfig } from 'axios';
import { getToken, getUserDetails } from '../../utilities/sessionHelper';

// Types for API Request
export interface ForecastRevenueRequest {
  businessUnits: string[];
  clients: string[];
  departments: string[];
  startYear: number;
  startQuarter: number;
  endYear: number;
  endQuarter: number;
  page?: number;
  size?: number;
}

// Types for API Response
export interface RevenueQuarters {
  [key: string]: number; // e.g., "Q1 2023": 61740.00
}

export interface RevenueTypeData {
  revenueType: string;
  quarters: RevenueQuarters;
  total: number;
}

export interface ForecastTableRow {
  businessUnit: string;
  client: string;
  department: string;
  revenueFromProject: RevenueTypeData;
  revenueFromPipeline: RevenueTypeData;
  actual: RevenueTypeData;
}

export interface ForecastRevenueResponse {
  'Table Data': ForecastTableRow[];
  'Total Forecasted Revenue (Projects + Pipeline)': RevenueTypeData;
  'Total Actual Revenue': RevenueTypeData;
  'Total Forecasted Revenue from Projects'?: RevenueTypeData;
  'Total Forecasted Revenue from Pipeline'?: RevenueTypeData;
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
  last?: boolean;
}

// Filter Options Response
export interface FilterOptionsResponse {
  businessUnits: string[];
  clients: string[];
  departments: string[];
}

// State interface
interface RevenueForecastState {
  data: ForecastRevenueResponse | null;
  loading: boolean;
  error: string | null;
  loadingMore: boolean;
  currentPage: number;
  salesData: ForecastRevenueResponse | null;
  salesLoading: boolean;
  salesError: string | null;
  salesLoadingMore: boolean;
  salesCurrentPage: number;
  filterOptions: FilterOptionsResponse | null;
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  exportLoading: boolean;
  exportError: string | null;
}

const initialState: RevenueForecastState = {
  data: null,
  loading: false,
  error: null,
  loadingMore: false,
  currentPage: 0,
  salesData: null,
  salesLoading: false,
  salesError: null,
  salesLoadingMore: false,
  salesCurrentPage: 0,
  filterOptions: null,
  filterOptionsLoading: false,
  filterOptionsError: null,
  exportLoading: false,
  exportError: null,
};

// Async thunk for fetching forecast revenue
export const fetchForecastRevenue = createAsyncThunk<
  ForecastRevenueResponse,
  ForecastRevenueRequest,
  { rejectValue: string }
>(
  'revenueForecast/fetchForecastRevenue',
  async (payload, { rejectWithValue }) => {
    try {
      const axiosConfig = {
        headers: {
          'Authorization': 'Bearer ' + getToken(),
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'loggedInUser': getUserDetails()?.email
        },
        withCredentials: true
      };

      const response = await axios.post<ForecastRevenueResponse>(
        'workorder/api/revenue/financeView/forecastRevenue',
        payload,
        axiosConfig
      );

      if (response.status !== 200) {
        return rejectWithValue('Failed to fetch forecast revenue data');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for loading more finance view data
export const loadMoreForecastRevenue = createAsyncThunk<
  ForecastRevenueResponse,
  ForecastRevenueRequest,
  { rejectValue: string }
>(
  'revenueForecast/loadMoreForecastRevenue',
  async (payload, { rejectWithValue }) => {
    try {
      const axiosConfig = {
        headers: {
          'Authorization': 'Bearer ' + getToken(),
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'loggedInUser': getUserDetails()?.email
        },
        withCredentials: true
      };

      const response = await axios.post<ForecastRevenueResponse>(
        'workorder/api/revenue/financeView/forecastRevenue',
        payload,
        axiosConfig
      );

      if (response.status !== 200) {
        return rejectWithValue('Failed to load more forecast revenue data');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching filter options
export const fetchFilterOptions = createAsyncThunk<
  FilterOptionsResponse,
  void,
  { rejectValue: string }
>(
  'revenueForecast/fetchFilterOptions',
  async (_, { rejectWithValue }) => {
    try {
      const axiosConfig = {
        headers: {
          'Authorization': 'Bearer ' + getToken(),
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'loggedInUser': getUserDetails()?.email
        },
        withCredentials: true
      };

      const response = await axios.get<FilterOptionsResponse>(
        'workorder/api/revenue/financeView/allBUsClientsDepts',
        axiosConfig
      );

      if (response.status !== 200) {
        return rejectWithValue('Failed to fetch filter options');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching sales view forecast revenue
export const fetchSalesViewRevenue = createAsyncThunk<
  ForecastRevenueResponse,
  ForecastRevenueRequest,
  { rejectValue: string }
>(
  'revenueForecast/fetchSalesViewRevenue',
  async (payload, { rejectWithValue }) => {
    try {
      const axiosConfig = {
        headers: {
          'Authorization': 'Bearer ' + getToken(),
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'loggedInUser': getUserDetails()?.email
        },
        withCredentials: true
      };

      const response = await axios.post<ForecastRevenueResponse>(
        'workorder/api/revenue/salesView/forecastRevenue',
        payload,
        axiosConfig
      );

      if (response.status !== 200) {
        return rejectWithValue('Failed to fetch sales view forecast revenue data');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for loading more sales view data
export const loadMoreSalesViewRevenue = createAsyncThunk<
  ForecastRevenueResponse,
  ForecastRevenueRequest,
  { rejectValue: string }
>(
  'revenueForecast/loadMoreSalesViewRevenue',
  async (payload, { rejectWithValue }) => {
    try {
      const axiosConfig = {
        headers: {
          'Authorization': 'Bearer ' + getToken(),
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'loggedInUser': getUserDetails()?.email
        },
        withCredentials: true
      };

      const response = await axios.post<ForecastRevenueResponse>(
        'workorder/api/revenue/salesView/forecastRevenue',
        payload,
        axiosConfig
      );

      if (response.status !== 200) {
        return rejectWithValue('Failed to load more sales view forecast revenue data');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for exporting finance view as Excel
export const exportFinanceViewAsExcel = createAsyncThunk<
  any,
  ForecastRevenueRequest,
  { rejectValue: string }
>(
  'revenueForecast/exportFinanceViewAsExcel',
  async (payload, { rejectWithValue }) => {
    try {
      const axiosConfig: AxiosRequestConfig<any> = {
        headers: {
          'Authorization': 'Bearer ' + getToken(),
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'loggedInUser': getUserDetails()?.email
        },
        withCredentials: true,
        responseType: 'blob',
      };

      const response = await axios.post(
        'workorder/api/revenue/financeView/exportRevenueAsExcel',
        payload,
        axiosConfig
      );

      if (response.status !== 200) {
        return rejectWithValue('Failed to export finance view data');
      }

      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || error.message);
    }
  }
);

// Async thunk for exporting sales view as Excel
export const exportSalesViewAsExcel = createAsyncThunk<
  any,
  ForecastRevenueRequest,
  { rejectValue: string }
>(
  'revenueForecast/exportSalesViewAsExcel',
  async (payload, { rejectWithValue }) => {
    try {
      const axiosConfig: AxiosRequestConfig<any> = {
        headers: {
          'Authorization': 'Bearer ' + getToken(),
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          'loggedInUser': getUserDetails()?.email
        },
        withCredentials: true,
        responseType: 'blob',
      };

      const response = await axios.post(
        'workorder/api/revenue/salesView/exportRevenueAsExcel',
        payload,
        axiosConfig
      );

      if (response.status !== 200) {
        return rejectWithValue('Failed to export sales view data');
      }

      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || error.message);
    }
  }
);

// Slice
const revenueForecastSlice = createSlice({
  name: 'revenueForecast',
  initialState,
  reducers: {
    clearForecastData: (state) => {
      state.data = null;
      state.error = null;
      state.currentPage = 0;
    },
    clearSalesData: (state) => {
      state.salesData = null;
      state.salesError = null;
      state.salesCurrentPage = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchForecastRevenue.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentPage = 0;
      })
      .addCase(fetchForecastRevenue.fulfilled, (state, action: PayloadAction<ForecastRevenueResponse>) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
        state.currentPage = action.payload.page || 0;
      })
      .addCase(fetchForecastRevenue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
      })
      .addCase(loadMoreForecastRevenue.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(loadMoreForecastRevenue.fulfilled, (state, action: PayloadAction<ForecastRevenueResponse>) => {
        state.loadingMore = false;
        if (state.data && action.payload['Table Data']) {
          state.data['Table Data'] = [...state.data['Table Data'], ...action.payload['Table Data']];
          state.data.page = action.payload.page;
          state.data.last = action.payload.last;
        }
        state.currentPage = action.payload.page || 0;
      })
      .addCase(loadMoreForecastRevenue.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.payload || 'An error occurred';
      })
      .addCase(fetchFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action: PayloadAction<FilterOptionsResponse>) => {
        state.filterOptionsLoading = false;
        state.filterOptions = {
          businessUnits: [...action.payload.businessUnits].sort((a, b) => a.localeCompare(b)),
          clients: [...action.payload.clients].sort((a, b) => a.localeCompare(b)),
          departments: [...action.payload.departments].sort((a, b) => a.localeCompare(b)),
        };
        state.filterOptionsError = null;
      })
      .addCase(fetchFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload || 'An error occurred';
      })
      .addCase(fetchSalesViewRevenue.pending, (state) => {
        state.salesLoading = true;
        state.salesError = null;
        state.salesCurrentPage = 0;
      })
      .addCase(fetchSalesViewRevenue.fulfilled, (state, action: PayloadAction<ForecastRevenueResponse>) => {
        state.salesLoading = false;
        state.salesData = action.payload;
        state.salesError = null;
        state.salesCurrentPage = action.payload.page || 0;
      })
      .addCase(fetchSalesViewRevenue.rejected, (state, action) => {
        state.salesLoading = false;
        state.salesError = action.payload || 'An error occurred';
      })
      .addCase(loadMoreSalesViewRevenue.pending, (state) => {
        state.salesLoadingMore = true;
      })
      .addCase(loadMoreSalesViewRevenue.fulfilled, (state, action: PayloadAction<ForecastRevenueResponse>) => {
        state.salesLoadingMore = false;
        if (state.salesData && action.payload['Table Data']) {
          state.salesData['Table Data'] = [...state.salesData['Table Data'], ...action.payload['Table Data']];
          state.salesData.page = action.payload.page;
          state.salesData.last = action.payload.last;
        }
        state.salesCurrentPage = action.payload.page || 0;
      })
      .addCase(loadMoreSalesViewRevenue.rejected, (state, action) => {
        state.salesLoadingMore = false;
        state.salesError = action.payload || 'An error occurred';
      })
      .addCase(exportFinanceViewAsExcel.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportFinanceViewAsExcel.fulfilled, (state) => {
        state.exportLoading = false;
        state.exportError = null;
      })
      .addCase(exportFinanceViewAsExcel.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload || 'An error occurred';
      })
      .addCase(exportSalesViewAsExcel.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportSalesViewAsExcel.fulfilled, (state) => {
        state.exportLoading = false;
        state.exportError = null;
      })
      .addCase(exportSalesViewAsExcel.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload || 'An error occurred';
      });
  },
});

export const { clearForecastData, clearSalesData } = revenueForecastSlice.actions;
export default revenueForecastSlice.reducer;
